import React, { useEffect, useMemo, useState } from "react";
import Dropdown from "../../common/dropDown";
import {
  getAssignedMilestoneIds,
  stripGuid,
} from "./isfPlaceholderService";
import { fetchMilestoneById, normalizeMilestoneDetail } from "./isfMilestoneService";

export default function TemplateFolderAssignments({
  selectedFolderId,
  templateId,
  templatePlaceholders = [],
  milestoneOptions = [],
  milestoneLibraryLoading = false,
  canEdit,
  userData,
  onAssignMilestone,
}) {
  const [assigningMilestone, setAssigningMilestone] = useState(false);
  const [selectedMilestoneOption, setSelectedMilestoneOption] = useState(null);
  const [assignError, setAssignError] = useState("");

  const usedMilestoneIds = useMemo(
    () => getAssignedMilestoneIds(templatePlaceholders),
    [templatePlaceholders],
  );

  const availableMilestoneOptions = useMemo(
    () =>
      (milestoneOptions || []).filter((opt) => {
        const id = stripGuid(opt.value)?.toLowerCase();
        return id && !usedMilestoneIds.has(id);
      }),
    [milestoneOptions, usedMilestoneIds],
  );

  useEffect(() => {
    if (!selectedMilestoneOption?.value) {
      setAssignError("");
      return;
    }
    const id = stripGuid(selectedMilestoneOption.value)?.toLowerCase();
    if (id && !usedMilestoneIds.has(id)) {
      setAssignError("");
    }
  }, [usedMilestoneIds, selectedMilestoneOption]);

  const handleAssignMilestone = async () => {
    if (!selectedMilestoneOption?.value || !selectedFolderId) return;
    const milestoneId = stripGuid(selectedMilestoneOption.value);
    if (!milestoneId || usedMilestoneIds.has(milestoneId.toLowerCase())) {
      setAssignError("This milestone is already on the template.");
      return;
    }

    setAssignError("");
    setAssigningMilestone(true);
    try {
      const response = await fetchMilestoneById({
        userData,
        milestoneId,
      });
      const { detail, records } = normalizeMilestoneDetail(response);
      if (!detail?.MilestoneId) {
        setAssignError("Could not load milestone details.");
        return;
      }

      await onAssignMilestone({
        milestoneDetail: detail,
        records,
        templateFolderId: selectedFolderId,
      });
      setSelectedMilestoneOption(null);
    } catch (error) {
      setAssignError(error?.message || "Failed to assign milestone.");
    } finally {
      setAssigningMilestone(false);
    }
  };

  if (!selectedFolderId || !canEdit) return null;

  return (
    <div className="tpl-detail__section tpl-folder-assign">
      <h6 className="tpl-detail__section-title">Folder assignments</h6>
      <p className="tpl-detail__hint mb-2 text-muted small">
        Assign a published milestone from the library to this folder.
      </p>

      <div className="tpl-folder-assign__milestone">
        <label className="form-label small mb-1">Milestone from library</label>
        {milestoneLibraryLoading ? (
          <p className="text-muted small mb-0">Loading milestones…</p>
        ) : !(milestoneOptions || []).length ? (
          <p className="text-muted small mb-0">No published milestones available.</p>
        ) : !availableMilestoneOptions.length ? (
          <p className="text-muted small mb-0">All milestones are already assigned.</p>
        ) : (
          <div className="tpl-folder-assign__milestone-row">
            <Dropdown
              options={availableMilestoneOptions}
              isMulti={false}
              selected={selectedMilestoneOption}
              onSelect={(opt) => {
                setSelectedMilestoneOption(opt || null);
                setAssignError("");
              }}
              placeholder="Select milestone"
              disabled={assigningMilestone || !templateId}
            />
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={
                !templateId ||
                !selectedMilestoneOption?.value ||
                assigningMilestone
              }
              onClick={handleAssignMilestone}
            >
              {assigningMilestone ? "Adding…" : "Add"}
            </button>
          </div>
        )}
        {!templateId ? (
          <p className="text-muted small mt-1 mb-0">Save the template first to assign milestones.</p>
        ) : null}
        {assignError ? (
          <p className="text-danger small mt-1 mb-0">{assignError}</p>
        ) : null}
      </div>
    </div>
  );
}
